import { getAccessToken } from '@repo/auth/core';
import {
  AlertsManagerWidgetState,
  type AlertsManagerWidgetInitParameters,
  DashboardService,
  WellDirectoryService,
} from '@repo/go-widgets';
import { action, makeObservable, observable, reaction } from 'mobx';

import { defaultAgent } from 'src/api/agent';
import { GLOBAL_DEPTH_UNIT, WELL_DIRECTORY_SERVICE_API_URL } from 'src/api/consts';
import { wellLogsWidgetAgent } from 'src/api/wellLogsWidgetAgent';
import type { TGroup, TWellListCombinedData } from 'src/entities/tab/TabEntity';
import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import { requireService } from 'src/packages/di';
import { IS_PORTABLE_DEVICE } from 'src/packages/shared/consts/isPortableDevice';

import { setWellIdInGroup } from '../../utils/setWellIdInGroup';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';

import type { AlertsManagerWidgetEntity } from './AlertsManagerWidget.entity';
function getCorrectLocale(language: string): 'ru' | 'en' | 'es' {
  switch (language) {
    case 'ru':
    case 'en':
    case 'es':
      return language;
  }
  return 'ru';
}

export class AlertsManagerWidgetStore implements IWidgetStore {
  @observable isReady: boolean = false;
  @observable isGroupSelectOpen: boolean;

  private readonly alertsManagerEntity: AlertsManagerWidgetEntity;
  private readonly groupSelectStore: GroupSelectStore;
  private readonly initParams: AlertsManagerWidgetInitParameters;

  constructor(
    alertsManagerEntity: AlertsManagerWidgetEntity,
    groupSelectStore: GroupSelectStore,
    private readonly wellListPanel: TWellListCombinedData,
    private readonly theme = requireService('theme'),
    private readonly language = requireService('language'),
    private readonly userPreferencesService = requireService('userPreference'),
    private readonly authService = requireService('authService'),
  ) {
    this.alertsManagerEntity = alertsManagerEntity;
    this.groupSelectStore = groupSelectStore;
    this.isGroupSelectOpen = alertsManagerEntity.isGroupsDefaultOpened;

    const wellDirectoryApiService = new WellDirectoryService(defaultAgent, WELL_DIRECTORY_SERVICE_API_URL);
    const dashboardApiService = new DashboardService(wellLogsWidgetAgent);

    this.initParams = {
      agent: defaultAgent,
      wellDirectoryApiService: wellDirectoryApiService,
      dashboardApiService,
      directoriesService: requireService('directories'),
      notificationsService: requireService('notifications'),
      userData: this.authService.userInfo,
      getAccessToken: getAccessToken,
      defaultDepthUnit: GLOBAL_DEPTH_UNIT,
    };

    makeObservable(this);
  }

  @action.bound
  setReady(value: boolean): void {
    this.isReady = value;
  }

  @action.bound
  setGroupSelectOpen(value: boolean): void {
    this.isGroupSelectOpen = value;
  }

  @action.bound
  setIsGroupSelectOpen(value: boolean): void {
    this.isGroupSelectOpen = value;
  }

  @action.bound
  setGroupForAlertsManager(group: TGroup | null): void {
    this.alertsManagerEntity.setGroup(group ? group.id : null);
  }

  @action.bound
  enableEditMode(): void {
    if (IS_PORTABLE_DEVICE) return;

    this.alertsManagerEntity.state?.enterEditMode();
  }

  private getNewState(wellId: number) {
    return new AlertsManagerWidgetState(
      this.initParams,
      {
        wellId,
        width: 520,
        height: 500,
        locale: getCorrectLocale(this.language.language),
        theme: this.theme.theme,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
      },
      this.alertsManagerEntity.stateParams,
      {
        onInitializedSuccess: () => {
          this.setReady(true);
        },
        onInternalStateChanged: (state) => {
          this.alertsManagerEntity.setStateParams(state);
        },
        onEditModeChanged: (isEditMode) => {
          this.alertsManagerEntity.setEditMode(isEditMode);
        },
      },
    );
  }

  effect = () => {
    const disposeGroupId = reaction(
      () => this.alertsManagerEntity.groupId,
      (groupId) => {
        if (groupId === null) return;
        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
        setWellIdInGroup(widgets, this.alertsManagerEntity, this.wellListPanel);
      },
    );

    const disposeWellId = reaction(
      () => this.alertsManagerEntity.wellId,
      (wellId, prevWellId) => {
        if (wellId && !this.alertsManagerEntity.state) {
          const newState = this.getNewState(wellId);
          this.alertsManagerEntity.setState(newState);
          this.setReady(true);
        } else if (wellId && this.alertsManagerEntity.state && typeof prevWellId !== 'undefined') {
          this.alertsManagerEntity.state.setAppParams({ wellId });
          this.alertsManagerEntity.setEditMode(false);
        } else if (!wellId && this.alertsManagerEntity.state) {
          this.alertsManagerEntity.state.destroy();
          this.alertsManagerEntity.setState(null);
          this.setReady(false);
        }
      },
      { fireImmediately: true },
    );

    const disposeStateParams = reaction(
      () => ({
        theme: this.theme.theme,
        language: this.language.language,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
      }),
      ({ theme, language, isAltNames }) => {
        this.alertsManagerEntity.state?.setAppParams({
          theme,
          locale: getCorrectLocale(language),
          isAltNames,
        });
      },
      { fireImmediately: true },
    );

    const wellListPanelDisposer = reaction(
      () => this.wellListPanel.store.selectedWellId,
      (wellId) => {
        if (this.wellListPanel.panelState?.groupId === this.alertsManagerEntity.groupId) {
          this.alertsManagerEntity.setWellId(wellId);
        }
      },
      { fireImmediately: true },
    );

    return () => {
      disposeGroupId();
      disposeWellId();
      disposeStateParams();
      wellListPanelDisposer();
    };
  };

  @action.bound
  exitFullScreen() {
    this.alertsManagerEntity.setFullscreen(false);
  }
}
